const getStringMessage = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const getObjectMessage = (value: any): string | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const detail = getStringMessage(value.detail);
    if (detail) {
        return detail;
    }

    const message = getStringMessage(value.message);
    if (message) {
        return message;
    }

    const title = getStringMessage(value.title);
    if (title) {
        return title;
    }

    if (value.errors && typeof value.errors === 'object') {
        const validationMessages = Object.values(value.errors)
            .flatMap((entry: any) => Array.isArray(entry) ? entry : [entry])
            .map(getStringMessage)
            .filter((entry): entry is string => !!entry);

        if (validationMessages.length > 0) {
            return validationMessages.join(' ');
        }
    }

    return undefined;
};

const tryParseJsonMessage = (text: string): string | undefined => {
    try {
        return getObjectMessage(JSON.parse(text));
    } catch {
        return undefined;
    }
};

const isHtmlResponse = (text: string) => /^<!doctype html|^<html[\s>]/i.test(text.trim());

const readBlobMessage = async (blob: Blob): Promise<string | undefined> => {
    const text = (await blob.text()).trim();
    if (!text || isHtmlResponse(text)) {
        return undefined;
    }

    return tryParseJsonMessage(text) || text;
};

const readArrayBufferMessage = (buffer: ArrayBuffer): string | undefined => {
    const text = new TextDecoder().decode(buffer).trim();
    if (!text || isHtmlResponse(text)) {
        return undefined;
    }

    return tryParseJsonMessage(text) || text;
};

export const getApiErrorMessage = async (error: any, fallback: string): Promise<string> => {
    const responseData = error?.response?.data;

    if (typeof Blob !== 'undefined' && responseData instanceof Blob) {
        const blobMessage = await readBlobMessage(responseData);
        if (blobMessage) {
            return blobMessage;
        }
    }

    if (typeof ArrayBuffer !== 'undefined' && responseData instanceof ArrayBuffer) {
        const bufferMessage = readArrayBufferMessage(responseData);
        if (bufferMessage) {
            return bufferMessage;
        }
    }

    const dataMessage = getStringMessage(responseData) || getObjectMessage(responseData);
    if (dataMessage) {
        return dataMessage;
    }

    const axiosMessage = getStringMessage(error?.message);
    return axiosMessage || fallback;
};
